import React, { useState, useEffect } from "react";
import { Users, Search, Edit2, Check, X, AlertTriangle } from "lucide-react";
import { useUserRole } from "../hooks/useUserRoleSecure";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import AdminConfigWarning from "./AdminConfigWarning";
import "./UserManagementTable.css";

/**
 * UserManagementTable component
 * Displays and manages user roles for admin users
 */
const UserManagementTable = () => {
  const { user: currentUser } = useAuth();
  const {
    fetchAllUsers,
    updateUserRole,
    updateUserFullName,
    isAdminConfigured,
  } = useUserRole();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "user_created_at",
    direction: "desc",
  });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingNameUserId, setEditingNameUserId] = useState(null);
  const [updatingUsers, setUpdatingUsers] = useState(new Set());
  const [selectedRole, setSelectedRole] = useState("");
  const [editedFullName, setEditedFullName] = useState("");

  const roles = [
    { value: "admin", label: "Admin", color: "admin" },
    {
      value: "operations_manager",
      label: "Operations Manager",
      color: "operations",
    },
    {
      value: "model_building_team",
      label: "Model Building Team",
      color: "member",
    },
    { value: "takeoffs", label: "Takeoffs", color: "takeoffs" },
    { value: "office_admin", label: "Office Admin", color: "viewer" },
    { value: "deactivated", label: "Deactivated", color: "deactivated" },
  ];

  // Load users data
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await fetchAllUsers();

      if (fetchError) {
        throw new Error(fetchError);
      }

      setUsers(data || []);
    } catch (err) {
      console.error("Error loading users:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aValue = a[sortConfig.key] || "";
    const bValue = b[sortConfig.key] || "";

    if (sortConfig.direction === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "desc"
          ? "asc"
          : "desc",
    }));
  };

  const getSortArrow = (columnKey) => {
    if (sortConfig.key !== columnKey) return "↕️";
    return sortConfig.direction === "asc" ? "↗️" : "↘️";
  };

  const getRoleClass = (role) => {
    const roleClassMap = {
      admin: "role-admin",
      operations_manager: "role-operations",
      model_building_team: "role-member",
      takeoffs: "role-takeoffs",
      office_admin: "role-viewer",
      member: "role-member", // Legacy support
      viewer: "role-viewer", // Legacy support
      deactivated: "role-deactivated",
    };
    return roleClassMap[role] || "role-default";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    try {
      const date = new Date(dateString);
      // Format as MM/DD/YY
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${day}/${year}`;
    } catch {
      return "Invalid date";
    }
  };

  const handleEditRole = (userId, currentRole) => {
    setEditingUserId(userId);
    setSelectedRole(currentRole || "model_building_team");
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setSelectedRole("");
  };

  const handleEditName = (userId, currentName) => {
    setEditingNameUserId(userId);
    setEditedFullName(currentName || "");
  };

  const handleCancelNameEdit = () => {
    setEditingNameUserId(null);
    setEditedFullName("");
  };

  const handleSaveName = async (userId) => {
    if (!editedFullName.trim()) {
      handleCancelNameEdit();
      return;
    }

    const updateKey = `name-${userId}`;
    setUpdatingUsers((prev) => new Set([...prev, updateKey]));

    try {
      // Optimistic update
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? { ...user, full_name: editedFullName.trim() }
            : user,
        ),
      );

      const result = await updateUserFullName(userId, editedFullName.trim());

      if (!result.success) {
        throw new Error(result.message);
      }

      // Reload data to get accurate information
      await loadUsers();
    } catch (err) {
      console.error("Error updating full name:", err);
      setError(`Failed to update full name: ${err.message}`);

      // Revert optimistic update
      await loadUsers();
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
      handleCancelNameEdit();
    }
  };

  const handleSaveRole = async (userId) => {
    if (
      !selectedRole ||
      selectedRole === users.find((u) => u.id === userId)?.role
    ) {
      handleCancelEdit();
      return;
    }

    // Validate role
    const validRoles = [
      "admin",
      "operations_manager",
      "model_building_team",
      "takeoffs",
      "office_admin",
      "deactivated",
    ];
    if (!validRoles.includes(selectedRole)) {
      setError("Invalid role selected");
      return;
    }

    // Safety check - prevent changing own role
    if (userId === currentUser?.id) {
      setError("Cannot change your own role");
      return;
    }

    const updateKey = userId;
    setUpdatingUsers((prev) => new Set([...prev, updateKey]));

    try {
      // Optimistic update
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId
            ? {
                ...user,
                role: selectedRole,
                role_updated_at: new Date().toISOString(),
              }
            : user,
        ),
      );

      const result = await updateUserRole(userId, selectedRole);

      if (!result.success) {
        throw new Error(result.message);
      }

      // Reload data to get accurate information
      await loadUsers();
    } catch (err) {
      console.error("Error updating role:", err);

      // More specific error messages
      let errorMessage = "Failed to update role";
      if (err.message.includes("Access denied")) {
        errorMessage = "Access denied. Admin privileges required.";
      } else if (err.message.includes("Cannot change your own role")) {
        errorMessage = "You cannot change your own role for security reasons.";
      } else if (err.message.includes("Invalid role")) {
        errorMessage = "Invalid role specified.";
      } else {
        errorMessage = `Failed to update role: ${err.message}`;
      }

      setError(errorMessage);

      // Revert optimistic update
      await loadUsers();
    } finally {
      setUpdatingUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(updateKey);
        return newSet;
      });
      handleCancelEdit();
    }
  };

  const canEditUser = (userId) => {
    return userId !== currentUser?.id; // Can't edit own role
  };

  // Check for configuration issues first
  if (!isAdminConfigured) {
    return <AdminConfigWarning onRetry={loadUsers} />;
  }

  if (loading) {
    return <LoadingSpinner message="Loading user data..." />;
  }

  return (
    <div className="user-management">
      {error && (
        <ErrorMessage
          message={error}
          onRetry={loadUsers}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Header */}
      <div className="user-management-header">
        <div className="header-info">
          <Users size={24} />
          <div>
            <h2>User Management</h2>
            <p>Manage user roles and permissions</p>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="user-management-controls">
        <div className="search-container">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search users by email, name, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="user-stats">
          <span className="stat">Total: {users.length}</span>
          <span className="stat">Showing: {filteredUsers.length}</span>
        </div>
      </div>

      {/* Users table */}
      <div className="table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("email")}>
                Email
                <span className="sort-arrow">{getSortArrow("email")}</span>
              </th>
              <th onClick={() => handleSort("full_name")}>
                Full Name
                <span className="sort-arrow">{getSortArrow("full_name")}</span>
              </th>
              <th onClick={() => handleSort("role")}>
                Role
                <span className="sort-arrow">{getSortArrow("role")}</span>
              </th>
              <th onClick={() => handleSort("user_created_at")}>
                Created
                <span className="sort-arrow">
                  {getSortArrow("user_created_at")}
                </span>
              </th>
              <th onClick={() => handleSort("last_sign_in_at")}>
                Last Sign In
                <span className="sort-arrow">
                  {getSortArrow("last_sign_in_at")}
                </span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => {
              const isUpdating = updatingUsers.has(user.id);
              const isEditing = editingUserId === user.id;
              const isCurrentUser = user.id === currentUser?.id;

              const isDeactivated = user.role === "deactivated";
              const rowClassName = [
                isCurrentUser ? "current-user" : "",
                isDeactivated ? "deactivated-user" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <tr key={user.id} className={rowClassName}>
                  <td>
                    <div className="user-email">
                      {user.email}
                      {isCurrentUser && (
                        <span className="current-user-badge">You</span>
                      )}
                      {isDeactivated && (
                        <span
                          className="deactivated-indicator"
                          title="Account Deactivated"
                        >
                          ⛔
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {editingNameUserId === user.id ? (
                      <div className="edit-actions">
                        <input
                          type="text"
                          value={editedFullName}
                          onChange={(e) => setEditedFullName(e.target.value)}
                          className="name-input"
                          disabled={updatingUsers.has(`name-${user.id}`)}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveName(user.id)}
                          disabled={updatingUsers.has(`name-${user.id}`)}
                          className="save-button"
                          title="Save"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={handleCancelNameEdit}
                          disabled={updatingUsers.has(`name-${user.id}`)}
                          className="cancel-button"
                          title="Cancel"
                        >
                          <X size={14} />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="editable-name">
                        <span>{user.full_name || "Not provided"}</span>
                        {canEditUser(user.id) && (
                          <button
                            onClick={() =>
                              handleEditName(user.id, user.full_name)
                            }
                            disabled={updatingUsers.has(`name-${user.id}`)}
                            className="edit-name-button"
                            title="Edit name"
                          >
                            <Edit2 size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        className="role-select"
                        disabled={isUpdating}
                      >
                        {roles.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`role-badge ${getRoleClass(user.role)}`}>
                        {user.role || "member"}
                        {isUpdating && (
                          <span className="updating-indicator">...</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td>{formatDate(user.user_created_at)}</td>
                  <td>{formatDate(user.last_sign_in_at)}</td>
                  <td>
                    {canEditUser(user.id) ? (
                      isEditing ? (
                        <div className="edit-actions">
                          <button
                            onClick={() => handleSaveRole(user.id)}
                            disabled={isUpdating}
                            className="save-button"
                          >
                            <Check size={14} />
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={isUpdating}
                            className="cancel-button"
                            title="Cancel changes"
                          >
                            <X size={14} />
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditRole(user.id, user.role)}
                          disabled={isUpdating}
                          className="edit-button"
                        >
                          <Edit2 size={14} />
                          Edit Role
                        </button>
                      )
                    ) : (
                      <div className="no-action">
                        <AlertTriangle size={14} />
                        <span>Cannot edit own role</span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {sortedUsers.length === 0 && (
          <div className="empty-state">
            <Users size={48} />
            <h3>No users found</h3>
            <p>
              {searchTerm
                ? `No users match "${searchTerm}"`
                : "No users available"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementTable;
